import { useTranslation } from 'react-i18next'
import { FC } from 'react'
import { Table } from '../components'
import {
    type MissingTranslation,
    TranslationModal,
    LogoModal,
    type LogoNames,
    type LogoAvailability,
    type LogoFlags,
} from '../components'
import request from '../../../services/request'
import useRequest, { RequestTypes } from '../../hooks/useRequest'
import { CertificateOwnership, Filters, Locale, CheckboxMapping, PaginationType } from '../types'
import { message } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { isChecked } from './helpers'
import { collectOwnershipErrors, downloadCertificateOwnership, downloadSticker } from './services'
import { getData } from '../services'
import { getTableColumns, TABLE_CONSTANTS } from './config'

const DEFAULT_PAG = TABLE_CONSTANTS.DEFAULT_PAGINATION

const OwnershipCertificate: FC = () => {
    const { t } = useTranslation()

    const [pagination, setPagination] = useState<PaginationType>(DEFAULT_PAG)
    const [filters, setFilters] = useState<Filters>({})

    const [missingTranslations, setMissingTranslations] = useState<MissingTranslation[]>([])
    const [translationModalVisible, setTranslationModalVisible] = useState(false)

    const [logoCheckboxMappings, setLogoCheckboxMappings] = useState<CheckboxMapping[]>([])
    const [newFormatCheckboxMappings, setNewFormatCheckboxMappings] = useState<CheckboxMapping[]>(
        [],
    )
    const [isGradientCheckboxMappings, setIsGradientCheckboxMappings] = useState<CheckboxMapping[]>(
        [],
    )
    const [isA3CheckboxMappings, setIsA3CheckboxMappings] = useState<CheckboxMapping[]>([])

    const [logoModalVisible, setLogoModalVisible] = useState(false)
    const [logoNames, setLogoNames] = useState<LogoNames | undefined>(undefined)
    const [logoAvailability, setLogoAvailability] = useState<LogoAvailability | undefined>(
        undefined,
    )
    const [logoModalIsNewFormat, setLogoModalIsNewFormat] = useState(false)

    const [pendingLogoRow, setPendingLogoRow] = useState<CertificateOwnership | null>(null)
    const [pendingLocale, setPendingLocale] = useState<Locale | null>(null)

    const {
        fetch: fetchOwnership,
        result: resultOwnership,
        isLoading: isLoadingOwnership,
    } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/',
        method: RequestTypes.get,
    })

    const { fetch: fetchOwnershipCSV, isLoading: isLoadingOwnershipCSV } = useRequest({
        link: '/api/extension/v1/files/certificates/ownership/csv',
        method: RequestTypes.get,
    })

    const { fetch: fetchTranslate } = useRequest({
        link: '/api/extension/v1/extension/certificates/translates/',
        method: RequestTypes.post,
    })

    useEffect(() => {
        getData(pagination, fetchOwnership)
    }, [])

    useEffect(() => {
        if (!missingTranslations.length) {
            setTranslationModalVisible(false)
        }
    }, [missingTranslations])

    const closeModal = () => {
        setTranslationModalVisible(false)
        setMissingTranslations([])
    }

    const updateFilters = (newFilters: Filters) => {
        const newPagination = {
            ...pagination,
            page: 1,
            filters: newFilters,
            total: resultOwnership?.pagination.count || 0,
        }
        setFilters(newFilters)
        setPagination(newPagination)
        getData(newPagination, fetchOwnership)
    }

    const handleDownloadClick = async (record: CertificateOwnership, locale: Locale) => {
        if (locale === 'en') {
            const { userMissing, operatorError } = await collectOwnershipErrors(record.id, 'en')
            if (operatorError) {
                message.error(
                    'Данных для формирования Свидетельства на английском языке недостаточно' +
                        ', обратитесь к оператору с использованием раздела "Мои заявки"',
                )
                return
            }
            if (userMissing.length) {
                setMissingTranslations(userMissing.map((text) => ({ text })))
                setTranslationModalVisible(true)
                return
            }
        }

        const wantLogo = isChecked(logoCheckboxMappings, record.id)
        if (wantLogo) {
            await openLogoModalForRow(record, locale)
            return
        }

        await downloadCertificateOwnership(
            record.id,
            locale,
            newFormatCheckboxMappings,
            isGradientCheckboxMappings,
            isA3CheckboxMappings,
            setMissingTranslations,
            setTranslationModalVisible,
            { client_logo: false },
        )
    }

    const handleStickerDownloadClick = async (record: CertificateOwnership) => {
        await downloadSticker(record.id, true)
        await downloadSticker(record.id, false)
    }

    const handleLogoSubmit = async (flags: LogoFlags): Promise<void> => {
        if (!pendingLogoRow || !pendingLocale) {
            return
        }
        await downloadCertificateOwnership(
            pendingLogoRow.id,
            pendingLocale,
            newFormatCheckboxMappings,
            isGradientCheckboxMappings,
            isA3CheckboxMappings,
            setMissingTranslations,
            setTranslationModalVisible,
            flags,
        )
        setLogoModalVisible(false)
        setPendingLogoRow(null)
        setPendingLocale(null)
    }

    const onSubmitTranslate = (ru: string, en: string, idx: number): void => {
        fetchTranslate({
            body: { ru, en },
            onSuccess: () => {
                message.success('Перевод успешно отправлен')
                setMissingTranslations((prev) => prev.filter((_, i) => i !== idx))
            },
            onError: () => {
                message.error('Ошибка при отправке перевода')
            },
        })
    }

    const openLogoModalForRow = async (
        record: CertificateOwnership,
        locale: Locale,
    ): Promise<void> => {
        try {
            const [clientAvailabilityReq, clientNamesReq, userAvailabilityReq, userNamesReq] =
                await Promise.all([
                    request.get(
                        '/api/extension/v1/extension/ownership_certificates/logos/available/',
                        {
                            params: { client_id: record.company_id },
                        },
                    ),
                    request.get(
                        '/api/extension/v1/extension/ownership_certificates/logos/owners/',
                        {
                            params: { client_id: record.company_id },
                        },
                    ),
                    request.get(
                        '/api/extension/v1/extension/ownership_certificates/logos/available/',
                        {
                            params: { client_id: record.user_id },
                        },
                    ),
                    request.get(
                        '/api/extension/v1/extension/ownership_certificates/logos/owners/',
                        {
                            params: { client_id: record.user_id },
                        },
                    ),
                ])

            setLogoAvailability({
                client_available: !!clientAvailabilityReq.data?.client_available,
                user_available: !!userAvailabilityReq?.data.client_available,
            })
            setLogoNames({
                client_name: clientNamesReq.data?.client_name,
                client_type: clientNamesReq.data?.client_type,
                client_id: record.company_id,
                user_name: userNamesReq.data?.client_name,
                user_type: userNamesReq.data?.client_type,
                user_id: record.user_id,
            })
            setLogoModalIsNewFormat(isChecked(newFormatCheckboxMappings, record.id))
            setPendingLogoRow(record)
            setPendingLocale(locale)
            setLogoModalVisible(true)
        } catch {
            message.error('Не удалось получить данные по логотипу клиента')
        }
    }

    const columns = useMemo(
        () =>
            getTableColumns(
                t,
                logoCheckboxMappings,
                newFormatCheckboxMappings,
                isGradientCheckboxMappings,
                isA3CheckboxMappings,
                setLogoCheckboxMappings,
                setNewFormatCheckboxMappings,
                setIsGradientCheckboxMappings,
                setIsA3CheckboxMappings,
                handleDownloadClick,
                handleStickerDownloadClick,
            ),
        [
            t,
            logoCheckboxMappings,
            newFormatCheckboxMappings,
            isGradientCheckboxMappings,
            isA3CheckboxMappings,
            handleDownloadClick,
        ],
    )

    const dataSource: CertificateOwnership[] = useMemo(() => {
        if (isLoadingOwnership || !resultOwnership || !resultOwnership.items) {
            return []
        }
        setPagination((pag) => {
            pag.total = resultOwnership?.pagination.count || 0
            return pag
        })
        const data = resultOwnership.items
        if (data && data.length > 0) {
            setLogoCheckboxMappings(() => {
                const newMap: CheckboxMapping[] = []
                for (const row of data as CertificateOwnership[]) {
                    newMap.push({ id: row.id, checked: false })
                }
                return newMap
            })
            setIsGradientCheckboxMappings(() => {
                const newMap: CheckboxMapping[] = []
                for (const row of data as CertificateOwnership[]) {
                    newMap.push({ id: row.id, checked: false })
                }
                return newMap
            })
            setNewFormatCheckboxMappings(() => {
                const newMap: CheckboxMapping[] = []
                for (const row of data as CertificateOwnership[]) {
                    newMap.push({ id: row.id, checked: false })
                }
                return newMap
            })
            setIsA3CheckboxMappings(() => {
                const newMap: CheckboxMapping[] = []
                for (const row of data as CertificateOwnership[]) {
                    newMap.push({ id: row.id, checked: false })
                }
                return newMap
            })
        }
        return (
            data?.map((ownershipCert: CertificateOwnership) => ({
                ...ownershipCert,
                key: `id${ownershipCert.id}`,
            })) || []
        )
    }, [isLoadingOwnership, resultOwnership])

    return (
        <>
            <Table
                isLoadingOwnership={isLoadingOwnership}
                isLoadingOwnershipCSV={isLoadingOwnershipCSV}
                dataSource={dataSource}
                filters={filters}
                columns={columns}
                pagination={pagination}
                resultOwnership={resultOwnership}
                fetchOwnership={fetchOwnership}
                fetchOwnershipCSV={fetchOwnershipCSV}
                updateFilters={updateFilters}
                setPagination={setPagination}
            />
            <TranslationModal
                visible={translationModalVisible}
                items={missingTranslations}
                onClose={closeModal}
                onSubmit={onSubmitTranslate}
            />
            <LogoModal
                visible={logoModalVisible}
                names={logoNames}
                availability={logoAvailability}
                onClose={() => {
                    setLogoModalVisible(false)
                    setPendingLogoRow(null)
                    setPendingLocale(null)
                }}
                onSubmit={handleLogoSubmit}
                isNewFormat={logoModalIsNewFormat}
            />
        </>
    )
}

export default OwnershipCertificate
